import { ErrorAlert, FieldError } from '@bayesprep/shared-ui/errors/components';
import { focusFirstInvalidField, normalizeApiError, type ApiError } from '@bayesprep/shared-ui/errors';
import '@bayesprep/shared-ui/presentation/login.css';
import { APPLICATION_VERSION } from '@bayesprep/shared-ui/version';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { InstitutionSelection, useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, Eye, EyeOff, LogIn } from 'lucide-react';
import StudentLogo from '@/components/StudentLogo';

import { LoginFormData, LoginField, loginFields, loginFieldError, normalizeLogin } from '@/utils/loginFeedback';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<LoginField, string>>>({});
  const inputRefs = useRef<Record<LoginField, HTMLInputElement | null>>({email: null, password: null});
  const errorRef = useRef<HTMLDivElement>(null);
  const inFlight = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [selection, setSelection] = useState<InstitutionSelection | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { login, selectInstitution } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Check for success message from email verification
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
    }
  }, [location.state]);


  const validateField = (field: LoginField, value: string) =>
    loginFieldError(field, value, !inputRefs.current.email?.validity.typeMismatch);

  const handleInputChange = (field: LoginField, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); setFailure(null);
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleBlur = (field: LoginField, event: React.FocusEvent<HTMLInputElement>) => {
    // Do not move a navigation link between pointer-down and click by inserting field errors.
    if (event.relatedTarget instanceof Element && event.relatedTarget.closest('a[href]')) return;
    setFieldErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    if (inFlight.current) return;
    setError(''); setFailure(null);
    const errors: Partial<Record<LoginField, string>> = {};
    loginFields.forEach(field => { const message = validateField(field, formData[field]); if (message) errors[field] = message; });
    setFieldErrors(errors);
    const firstInvalid = loginFields.find(field => errors[field]);
    if (firstInvalid) { inputRefs.current[firstInvalid]?.focus(); return; }

    const normalized = normalizeLogin(formData);
    setFormData(normalized);
    inFlight.current = true;
    setIsLoading(true);
    try {
      const result = await login(normalized.email, normalized.password);
      if (result?.selectionToken) { setSelection(result); setFormData(prev => ({ ...prev, password: '' })); }
    } catch (error: unknown) {
      const failure = normalizeApiError(error, { login: true, fallback: 'Unable to sign in. Check your details or contact your institution.' });
      setFailure(failure);
      setFieldErrors({ email: failure.fieldErrors.email?.join(' '), password: failure.fieldErrors.password?.join(' ') });
      requestAnimationFrame(() => { if (!focusFirstInvalidField(form, failure.fieldErrors)) errorRef.current?.focus(); });
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  };

  const chooseInstitution = async (tenantId: string) => {
    if (!selection || inFlight.current) return;
    inFlight.current = true; setIsLoading(true); setError(''); setFailure(null);
    try { await selectInstitution(selection.selectionToken, tenantId); }
    catch { setError('Unable to select this institution. Sign in again to retry.'); setSelection(null); }
    finally { inFlight.current = false; setIsLoading(false); }
  };

  // Consolidated Login Form
  const renderLoginForm = () => (
    <div className="bp-login flex flex-1 items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <div className="bp-login-stack w-full max-w-md space-y-8">
        <div className="bp-login-header text-center">
          <div className="flex justify-center mb-6">
            <StudentLogo size="xl" showText={true} textColor="default" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Student sign in</h1>
          <p className="mt-2 text-sm text-muted-foreground font-body">
            Sign in to access your account
          </p>
        </div>

        <div className="bp-login-card mt-8 bg-card py-8 px-6 shadow-sm rounded-xl border border-border">
          {successMessage && (
            <div role="status" className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle aria-hidden="true" className="h-5 w-5 text-green-600 mr-3" />
                <span className="text-sm text-green-700 font-medium">{successMessage}</span>
              </div>
            </div>
          )}

          {(error || failure) && <ErrorAlert ref={errorRef} failure={failure} message={error} className="mb-6" />}

          {selection ? (
            <section aria-labelledby="institution-heading" className="space-y-4">
              <h2 id="institution-heading" className="text-lg font-semibold">Choose your institution</h2>
              <p className="text-sm text-muted-foreground">Choose where you want to use the Student portal.</p>
              {selection.institutions.filter(item => item.roles.includes('ROLE_STUDENT')).map(item => (
                <Button key={item.tenantId} type="button" className="h-auto min-h-10 w-full whitespace-normal break-words" disabled={isLoading} onClick={() => chooseInstitution(item.tenantId)}>{item.institutionName}</Button>
              ))}
              {!selection.institutions.some(item => item.roles.includes('ROLE_STUDENT')) && <p role="alert">None of your memberships have Student portal access.</p>}
              <Button type="button" variant="outline" disabled={isLoading} onClick={() => setSelection(null)}>Back to sign in</Button>
            </section>
          ) : <form noValidate aria-busy={isLoading} onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="block text-sm font-semibold text-foreground font-heading" required>
                Email Address
              </Label>
              <Input
                id="email"
                name="email"
                ref={element => { inputRefs.current.email = element; }}
                onBlur={event => handleBlur('email', event)}
                aria-invalid={Boolean(fieldErrors.email)}
                disabled={isLoading}
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                autoFocus
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                aria-describedby={fieldErrors.email ? 'email-help email-error' : 'email-help'}
                className="font-body"
              />
              <p id="email-help" className="text-xs text-muted-foreground">Use the email address registered with your institution.</p>
              <FieldError id="email-error" message={fieldErrors.email} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="block text-sm font-semibold text-foreground font-heading" required>
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  ref={element => { inputRefs.current.password = element; }}
                  onBlur={event => handleBlur('password', event)}
                  aria-invalid={Boolean(fieldErrors.password)}
                  disabled={isLoading}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                  className="font-body pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  disabled={isLoading}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-primary hover:text-primary/80 transition-colors duration-200"
                >
                  {showPassword ? (
                    <EyeOff aria-hidden="true" className="h-5 w-5" />
                  ) : (
                    <Eye aria-hidden="true" className="h-5 w-5" />
                  )}
                </button>
              </div>
              <FieldError id="password-error" message={fieldErrors.password} />
            </div>

            <p className="text-xs text-muted-foreground">All fields are required. Enter your details, then choose Sign In.</p>
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 aria-hidden="true" className="mr-3 h-5 w-5 animate-spin" />
                  <span className="font-medium">Signing you in...</span>
                </>
              ) : (
                <><LogIn className="mr-2 h-5 w-5" aria-hidden="true" />Sign In</>
              )}
            </Button>
          </form>}

          <div className="mt-8 text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-200 font-body"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Version Display */}
          <div className="mt-6 text-center text-xs text-muted-foreground font-body">
            Student Portal · <span aria-label="Application version">Version {APPLICATION_VERSION}</span>
          </div>
        </div>
      </div>
    </div>
  );



  return renderLoginForm();
};

export default LoginPage;
